import { useState } from "react";
import { Tooltip } from "flowbite-react";
import { HiPlus, HiSaveAs, HiTrendingUp } from "react-icons/hi";
import * as Sentry from "@sentry/react";
import { useTranslation, useAuthentication, useToast } from "@repo/core";
import { useIndicatorKey, type Strategy } from "@repo/api";
import { useStrategyDetailType } from "../../../hooks/useStrategyDetailType";
import RoboMarketsModal from "./roboMarkets";
import StrategySlotSelectionModal from "../overwriteModal/StrategySlotSelectionModal";
import { usePortfolioActions } from "../hooks/usePortfolioActions";
import { ConfirmationModal } from "../confirmationModal";

interface StrategyActionButtonsProps {
  strategy?: Strategy;
  dateRange?: string;
  className?: string;
  layout?: "mobile" | "desktop";
}

/**
 * Strategy action buttons component
 * Handles Add to Overview, Trade on RoboMarkets, and future action buttons
 */
export const StrategyActionButtons = ({
  strategy,
  dateRange,
  className = "",
  layout = "desktop",
}: StrategyActionButtonsProps) => {
  const { tCommon } = useTranslation();
  const { isAuthenticated } = useAuthentication();
  const { addErrorMessage } = useToast();
  const { universe } = useIndicatorKey();
  const { addToOverview, saveAsActive, isCloning } = usePortfolioActions();

  // Use the strategy detail type hook to determine button visibility
  const { actions } = useStrategyDetailType(strategy);

  const [showRoboMarketsModal, setShowRoboMarketsModal] = useState(false);
  const [showSlotSelectionModal, setShowSlotSelectionModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingCloneData, setPendingCloneData] = useState<{
    modelId: string;
    universe: string;
    period: string;
  } | null>(null);

  // Determine button visibility based on strategy detail type and authentication
  const shouldShowAddToOverview = () => {
    return isAuthenticated && actions.showAddToOverview;
  };

  const shouldShowRoboMarkets = () => {
    return actions.showTradeOnRoboMarkets && strategy;
  };

  const handleAddToOverview = () => {
    // Track Add to Overview button click in Sentry
    Sentry.addBreadcrumb({
      category: 'strategy',
      message: 'User clicked Add to Overview button',
      level: 'info',
      data: {
        strategyId: strategy?.strategyId,
        modelId: strategy?.modelId,
        universe,
        period: dateRange
      }
    });

    const payload = {
      modelId: strategy?.modelId || "draft",
      universe: universe || "",
      period: dateRange?.toUpperCase() || "",
    };
    setPendingCloneData(payload);

    addToOverview({
      payload,
      preventErrorMessage: true,
      customError: (error) => {
        if (
          error &&
          typeof error === "object" &&
          "status" in error &&
          "message" in error &&
          (error as { status: number; message: string }).status === 403 &&
          (error as { status: number; message: string }).message?.includes(
            "No empty slot available"
          )
        ) {
          // Store the clone data and show slot selection modal
          setShowSlotSelectionModal(true);
        } else {
          addErrorMessage(
            "Failed to add strategy to overview. Please try again."
          );
        }
      },
    });
  };

  const handleSaveAsActiveClick = () => {
    const payload = {
      modelId: strategy?.modelId || "",
      newModelId: `${strategy?.modelId?.replace("_draft", "")}`,
      overwriteExisting: true,
      deleteSource: true,
    };

    saveAsActive({ payload });
  };

  const handleRoboMarketsClick = () => {
    setShowRoboMarketsModal(true);
  };

  const handleRoboMarketsClose = () => {
    setShowRoboMarketsModal(false);
  };

  const handleSlotSelectionModalClose = () => {
    setShowSlotSelectionModal(false);
    setPendingCloneData(null);
  };

  // Mobile layout
  if (layout === "mobile") {
    return (
      <div className={`space-y-2 ${className}`}>
        {shouldShowAddToOverview() && (
          <div>
            <button
              onClick={handleAddToOverview}
              disabled={isCloning}
              className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              data-tour="add-to-overview-button-mobile"
            >
              <HiPlus size={16} />
              <span className="text-sm font-medium">
                {isCloning ? "Adding..." : tCommon("addToOverview")}
              </span>
            </button>
          </div>
        )}

        {actions.showTradeOnRoboMarkets && (
          <div>
            <button
              onClick={handleRoboMarketsClick}
              className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
            >
              <HiTrendingUp size={16} />
              <span className="text-sm font-medium">
                {tCommon("tradeOnRoboMarkets")}
              </span>
            </button>
          </div>
        )}
        {actions.showSaveAsActive && (
          <Tooltip
            content="You are watching unsaved changes in a draft. Save to persist portfolio changes."
            placement="top"
          >
            <button
              onClick={handleSaveAsActiveClick}
              className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
            >
              <HiTrendingUp size={16} />
              <span className="text-sm font-medium">
                {tCommon("saveAsActive")}
              </span>
            </button>
          </Tooltip>
        )}

        {/* Modals */}
        <RoboMarketsModal
          isOpen={showRoboMarketsModal}
          onClose={handleRoboMarketsClose}
          strategy={strategy}
        />

        <StrategySlotSelectionModal
          isOpen={showSlotSelectionModal}
          onClose={handleSlotSelectionModalClose}
          cloneData={
            pendingCloneData || { modelId: "", universe: "", period: "" }
          }
        />
      </div>
    );
  }

  // Desktop layout
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {shouldShowAddToOverview() && (
        <button
          onClick={handleAddToOverview}
          disabled={isCloning}
          className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          data-tour="add-to-overview-button"
        >
          <HiPlus size={16} />
          <span className="text-sm font-medium">
            {isCloning ? "Adding..." : tCommon("addToOverview")}
          </span>
        </button>
      )}

      {shouldShowRoboMarkets() && (
        <button
          onClick={handleRoboMarketsClick}
          disabled={strategy?.strategyId === "admin_stoxx600_M"}
          title={
            strategy?.strategyId === "admin_stoxx600_M"
              ? "Automatic trading on STOXX Europe 600 currently not supported."
              : ""
          }
          className="cursor-pointer flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <HiTrendingUp size={16} />
          <span className="text-sm font-medium">
            {tCommon("tradeOnRoboMarkets")}
          </span>
        </button>
      )}
      {actions.showSaveAsActive && (
        <Tooltip
          content="You are watching unsaved changes in a draft. Save to persist portfolio changes."
          placement="top"
        >
          <button
            onClick={() => setShowConfirmationModal(true)}
            className="flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
          >
            <HiSaveAs size={16} />
            <span className="text-sm font-medium">
              {tCommon("saveAsActive")}
            </span>
          </button>
        </Tooltip>
      )}

      {/* Modals */}
      <RoboMarketsModal
        isOpen={showRoboMarketsModal}
        onClose={handleRoboMarketsClose}
        strategy={strategy}
      />

      <ConfirmationModal
        showConfirmationModal={showConfirmationModal}
        setShowConfirmationModal={setShowConfirmationModal}
        title={tCommon("saveAsActiveModal.title")}
        confirmationMessage={
          <>
            <p>{tCommon("saveAsActiveModal.body1")}</p>
            <p>{tCommon("saveAsActiveModal.body2")}</p>
            <p>{tCommon("saveAsActiveModal.body3")}</p>
          </>
        }
        cancelLabel={tCommon("saveAsActiveModal.cancel")}
        confirmLabel={tCommon("saveAsActiveModal.confirm")}
        isDisabled={isCloning}
        isContinueAvailable={true}
        onContinuePress={handleSaveAsActiveClick}
      />

      <StrategySlotSelectionModal
        isOpen={showSlotSelectionModal}
        onClose={handleSlotSelectionModalClose}
        cloneData={
          pendingCloneData || { modelId: "", universe: "", period: "" }
        }
      />
    </div>
  );
};

export default StrategyActionButtons;
